import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/env';

export interface FeatureFlags {
  // One flag per feature — gates the page route, its home card, and its nav item.
  feature_page_map: boolean;
  feature_page_damage_calc: boolean;
  feature_page_discord_bot: boolean;
  feature_page_pokedex: boolean;
  feature_page_team_builder: boolean;
  feature_page_sets: boolean;
  feature_page_type_chart: boolean;
  feature_page_games: boolean;
  feature_page_counter: boolean;
  feature_page_pvp: boolean;
  feature_page_dungeons: boolean;
  feature_page_database: boolean;
  feature_page_moves: boolean;
  feature_page_items: boolean;
  feature_page_abilities: boolean;
  feature_database_ai: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  feature_page_map: true,
  feature_page_damage_calc: true,
  feature_page_discord_bot: true,
  feature_page_pokedex: true,
  feature_page_team_builder: true,
  feature_page_sets: true,
  feature_page_type_chart: true,
  feature_page_games: true,
  feature_page_counter: true,
  feature_page_pvp: true,
  feature_page_dungeons: true,
  feature_page_database: true,
  feature_page_moves: true,
  feature_page_items: true,
  feature_page_abilities: true,
  feature_database_ai: true,
};

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  loading: boolean;
  /** Returns true if the feature is enabled OR if the current user is an admin (admins bypass all flags) */
  isEnabled: (key: keyof FeatureFlags, isAdmin?: boolean) => boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  // Hydrate synchronously from the last known flags so the UI paints correct
  // immediately instead of flashing defaults for the ~300ms /features round-trip.
  const cachedFlags = ((): FeatureFlags | null => {
    try {
      const s = localStorage.getItem('featureFlags:v1');
      return s ? { ...DEFAULT_FLAGS, ...JSON.parse(s) } : null;
    } catch { return null; }
  })();

  const [flags, setFlags] = useState<FeatureFlags>(cachedFlags ?? DEFAULT_FLAGS);
  const [loading, setLoading] = useState(!cachedFlags);

  const fetchFlags = useCallback(async () => {
    try {
      const response = await axios.get<FeatureFlags>(`${API_BASE_URL}/features`);
      const merged = { ...DEFAULT_FLAGS, ...response.data };
      setFlags(merged);
      try { localStorage.setItem('featureFlags:v1', JSON.stringify(response.data)); } catch { /* ignore */ }
    } catch {
      // On failure keep whatever we have (cache or defaults) so the site stays usable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const isEnabled = (key: keyof FeatureFlags, isAdmin = false): boolean => {
    if (isAdmin) return true;
    return flags[key] ?? true;
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, isEnabled, refresh: fetchFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
